// store/useTreeStore.ts — Tree canvas UI state (NOT persisted — resets on reload)

import { create } from 'zustand';
import type { TreeViewMode } from '@/types';

interface TreeActiveFilters {
  showShadow: boolean;
  showDeceased: boolean;
  generationRange: [number, number] | null;
}

interface TreeUIState {
  // ── Selection (opens bottom drawer) ──
  selectedNodeId: string | null;

  // ── Highlight (search result ring only — no drawer) ──
  highlightedNodeId: string | null;

  // ── Hover (desktop) / long-press (mobile) — gold ring + connected-edge glow, no drawer ──
  hoveredNodeId: string | null;

  // ── Connect picker — "tap two existing nodes to link them" mode ──
  connectPicker: {
    active: boolean;
    sourceId: string | null;
    /** Set once a second, distinct node is tapped — opens the relationship-type dialog. */
    targetId: string | null;
  };

  // ── Just-connected pair — drives the beacon + gold glow celebration for ~3s ──
  justConnected: { a: string; b: string } | null;

  // ── Unlink picker — "tap two connected nodes to remove their relationship" mode ──
  unlinkPicker: {
    active: boolean;
    sourceId: string | null;
    /** Set once a second, distinct node is tapped — opens the unlink-confirm dialog. */
    targetId: string | null;
  };

  // ── Remove picker — "tap a node to permanently remove it from the tree" mode ──
  removePicker: {
    active: boolean;
  };

  // ── Check-relation picker — "tap two people to see how they're related" mode ──
  checkPicker: {
    active: boolean;
    sourceId: string | null;
    /** Set once a second, distinct node is tapped — opens the relationship result dialog. */
    targetId: string | null;
  };

  // ── View mode ──
  viewMode: TreeViewMode;
  focusedNodeId: string | null; // Center node in focused branch
  /** Bumped on every "My Branch" click so the canvas re-centers on the
   *  viewer's own node even when the view mode didn't actually change. */
  recenterToken: number;

  // ── Canvas transform ──
  zoomLevel: number;
  panOffset: { x: number; y: number };

  // ── Filters ──
  activeFilters: TreeActiveFilters;

  // ── Search ──
  searchQuery: string;
  isSearchOpen: boolean;

  // ── Loading ──
  isCanvasReady: boolean;

  // ── Actions ──
  selectNode: (nodeId: string | null) => void;
  /** Pan canvas to a node and show a highlight ring. Does NOT open the bottom drawer. */
  setHighlightNode: (nodeId: string | null) => void;
  /** Hover (desktop) or long-press (mobile). Does NOT open the bottom drawer. */
  setHoveredNode: (nodeId: string | null) => void;
  /** Enter connect-picker mode. Pass a nodeId to pre-fill the source (e.g. from the action sheet). */
  beginConnect: (sourceId?: string) => void;
  /** Tapping a node while the picker is active — first tap sets source, second sets target. */
  pickConnectNode: (nodeId: string) => void;
  cancelConnect: () => void;
  /** Relationship created successfully — closes the picker/dialog and starts the celebration. */
  finishConnect: (a: string, b: string) => void;
  clearJustConnected: () => void;
  /** Enter unlink-picker mode. Pass a nodeId to pre-fill the source (e.g. from the action sheet). */
  beginUnlink: (sourceId?: string) => void;
  /** Tapping a node while the unlink picker is active — first tap sets source, second sets target. */
  pickUnlinkNode: (nodeId: string) => void;
  cancelUnlink: () => void;
  /** Relationship removed (or dialog dismissed) — closes the picker/dialog. */
  finishUnlink: () => void;
  /** Enter remove-picker mode — the next node tapped opens the remove-confirm dialog. */
  beginRemove: () => void;
  cancelRemove: () => void;
  /** Enter check-relation-picker mode. Pass a nodeId to pre-fill the source (e.g. from the action sheet). */
  beginCheck: (sourceId?: string) => void;
  /** Tapping a node while the check picker is active — first tap sets source, second sets target. */
  pickCheckNode: (nodeId: string) => void;
  /** Clears the picked pair but keeps the picker active — lets the user check another pair right away. */
  resetCheckPair: () => void;
  cancelCheck: () => void;
  setViewMode: (mode: TreeViewMode) => void;
  setFocusedNode: (nodeId: string) => void;
  /** Re-center the canvas on the viewer's own node ("My Branch"). */
  requestRecenter: () => void;
  setZoom: (zoom: number) => void;
  setPan: (offset: { x: number; y: number }) => void;
  updatePan: (delta: { x: number; y: number }) => void;
  setFilter: <K extends keyof TreeActiveFilters>(key: K, value: TreeActiveFilters[K]) => void;
  setSearch: (query: string) => void;
  setSearchOpen: (open: boolean) => void;
  setCanvasReady: (ready: boolean) => void;
  resetView: () => void;
}

const DEFAULT_FILTERS: TreeActiveFilters = {
  showShadow: true,
  showDeceased: true,
  generationRange: null,
};

export const useTreeStore = create<TreeUIState>()((set) => ({
  // Initial state
  selectedNodeId: null,
  highlightedNodeId: null,
  hoveredNodeId: null,
  connectPicker: { active: false, sourceId: null, targetId: null },
  justConnected: null,
  unlinkPicker: { active: false, sourceId: null, targetId: null },
  removePicker: { active: false },
  checkPicker: { active: false, sourceId: null, targetId: null },
  viewMode: 'FOCUSED_BRANCH',
  focusedNodeId: null,
  recenterToken: 0,
  zoomLevel: 1,
  panOffset: { x: 0, y: 0 },
  activeFilters: { ...DEFAULT_FILTERS },
  searchQuery: '',
  isSearchOpen: false,
  isCanvasReady: false,

  // Actions
  selectNode: (nodeId) =>
    // Selecting a node via tap clears the search highlight
    set({ selectedNodeId: nodeId, highlightedNodeId: null }),

  setHighlightNode: (nodeId) =>
    // Highlight from search: never touches selectedNodeId, drawer stays closed
    set({ highlightedNodeId: nodeId, selectedNodeId: null }),

  setHoveredNode: (nodeId) =>
    set({ hoveredNodeId: nodeId }),

  beginConnect: (sourceId) =>
    // Entering the picker closes the drawer so taps go to the picker instead
    // (and cancels unlink-picker mode, since only one picker can own taps at a time)
    set({
      connectPicker: { active: true, sourceId: sourceId ?? null, targetId: null },
      unlinkPicker: { active: false, sourceId: null, targetId: null },
      removePicker: { active: false },
      checkPicker: { active: false, sourceId: null, targetId: null },
      selectedNodeId: null,
    }),

  pickConnectNode: (nodeId) =>
    set((s) => {
      if (!s.connectPicker.active) return s;
      if (!s.connectPicker.sourceId) {
        return { connectPicker: { ...s.connectPicker, sourceId: nodeId } };
      }
      if (nodeId === s.connectPicker.sourceId) return s; // tapped the same node again — no-op
      return { connectPicker: { ...s.connectPicker, targetId: nodeId } };
    }),

  cancelConnect: () =>
    set({ connectPicker: { active: false, sourceId: null, targetId: null } }),

  finishConnect: (a, b) =>
    set({
      connectPicker: { active: false, sourceId: null, targetId: null },
      justConnected: { a, b },
    }),

  clearJustConnected: () =>
    set({ justConnected: null }),

  beginUnlink: (sourceId) =>
    // Entering the picker closes the drawer so taps go to the picker instead
    // (and cancels connect-picker mode, since only one picker can own taps at a time)
    set({
      unlinkPicker: { active: true, sourceId: sourceId ?? null, targetId: null },
      connectPicker: { active: false, sourceId: null, targetId: null },
      removePicker: { active: false },
      checkPicker: { active: false, sourceId: null, targetId: null },
      selectedNodeId: null,
    }),

  pickUnlinkNode: (nodeId) =>
    set((s) => {
      if (!s.unlinkPicker.active) return s;
      if (!s.unlinkPicker.sourceId) {
        return { unlinkPicker: { ...s.unlinkPicker, sourceId: nodeId } };
      }
      if (nodeId === s.unlinkPicker.sourceId) return s; // tapped the same node again — no-op
      return { unlinkPicker: { ...s.unlinkPicker, targetId: nodeId } };
    }),

  cancelUnlink: () =>
    set({ unlinkPicker: { active: false, sourceId: null, targetId: null } }),

  finishUnlink: () =>
    set({ unlinkPicker: { active: false, sourceId: null, targetId: null } }),

  beginRemove: () =>
    // Entering the picker closes the drawer so taps go to the picker instead
    // (and cancels the other pickers, since only one picker can own taps at a time)
    set({
      removePicker: { active: true },
      connectPicker: { active: false, sourceId: null, targetId: null },
      unlinkPicker: { active: false, sourceId: null, targetId: null },
      checkPicker: { active: false, sourceId: null, targetId: null },
      selectedNodeId: null,
    }),

  cancelRemove: () =>
    set({ removePicker: { active: false } }),

  beginCheck: (sourceId) =>
    // Entering the picker closes the drawer so taps go to the picker instead
    // (and cancels the other pickers, since only one picker can own taps at a time)
    set({
      checkPicker: { active: true, sourceId: sourceId ?? null, targetId: null },
      connectPicker: { active: false, sourceId: null, targetId: null },
      unlinkPicker: { active: false, sourceId: null, targetId: null },
      removePicker: { active: false },
      selectedNodeId: null,
    }),

  pickCheckNode: (nodeId) =>
    set((s) => {
      if (!s.checkPicker.active) return s;
      if (!s.checkPicker.sourceId) {
        return { checkPicker: { ...s.checkPicker, sourceId: nodeId } };
      }
      if (nodeId === s.checkPicker.sourceId) return s; // tapped the same node again — no-op
      return { checkPicker: { ...s.checkPicker, targetId: nodeId } };
    }),

  resetCheckPair: () =>
    set({ checkPicker: { active: true, sourceId: null, targetId: null } }),

  cancelCheck: () =>
    set({ checkPicker: { active: false, sourceId: null, targetId: null } }),

  setViewMode: (mode) =>
    set({ viewMode: mode }),

  setFocusedNode: (nodeId) =>
    set({ focusedNodeId: nodeId, selectedNodeId: null, highlightedNodeId: null }),

  requestRecenter: () =>
    set((s) => ({ recenterToken: s.recenterToken + 1 })),

  setZoom: (zoom) =>
    set({ zoomLevel: Math.min(2.5, Math.max(0.3, zoom)) }),

  setPan: (offset) =>
    set({ panOffset: offset }),

  updatePan: (delta) =>
    set((s) => ({
      panOffset: {
        x: s.panOffset.x + delta.x,
        y: s.panOffset.y + delta.y,
      },
    })),

  setFilter: (key, value) =>
    set((s) => ({
      activeFilters: { ...s.activeFilters, [key]: value },
    })),

  setSearch: (query) =>
    set({ searchQuery: query }),

  setSearchOpen: (open) =>
    set({ isSearchOpen: open, searchQuery: open ? '' : '' }),

  setCanvasReady: (ready) =>
    set({ isCanvasReady: ready }),

  resetView: () =>
    set({
      zoomLevel: 1,
      panOffset: { x: 0, y: 0 },
      selectedNodeId: null,
      highlightedNodeId: null,
      hoveredNodeId: null,
      connectPicker: { active: false, sourceId: null, targetId: null },
      justConnected: null,
      unlinkPicker: { active: false, sourceId: null, targetId: null },
      removePicker: { active: false },
      checkPicker: { active: false, sourceId: null, targetId: null },
      activeFilters: { ...DEFAULT_FILTERS },
    }),
}));
